import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context';
import { calendarApi, layoutApi, type CalEpisode } from '../api';
import { useMeta } from '../meta';
import { tmdbRef, type PlexItem } from '../plex';
import { usePrefs } from '../prefs';

/**
 * Screensaver zoals in Kodi: na een tijdje niets doen een diavoorstelling van achtergronden
 * uit je bibliotheek, met klok. Elke toets, klik of beweging stopt hem (zonder dat die toets iets anders doet).
 */
export function Screensaver({ disabled }: { disabled: boolean }) {
  const { plex, libraries } = useApp();
  const prefs = usePrefs();
  const [active, setActive] = useState(false);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [upcoming, setUpcoming] = useState<CalEpisode[] | null>(null);
  const [i, setI] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const timer = useRef<number | undefined>(undefined);
  const minutes = prefs.screensaverMinutes;

  // Wachten op inactiviteit
  useEffect(() => {
    if (!minutes || disabled) {
      setActive(false);
      return;
    }
    const arm = () => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        // Niet als er ergens een video speelt (bijv. een trailer met geluid)
        const playing = [...document.querySelectorAll('video')].some((v) => !v.paused);
        if (!playing && document.visibilityState === 'visible') setActive(true);
        else arm();
      }, minutes * 60_000);
    };
    const onActivity = () => {
      if (!active) arm();
    };
    arm();
    const events = ['keydown', 'pointermove', 'pointerdown', 'wheel', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      window.clearTimeout(timer.current);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [minutes, disabled, active]);

  // Stoppen: de eerste toets/klik wordt "opgegeten"
  useEffect(() => {
    if (!active) return;
    const stop = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      setActive(false);
    };
    const moved = { x: -1, y: -1 };
    const onMove = (e: PointerEvent) => {
      // Kleine trillingen van de muis negeren
      if (moved.x < 0) {
        moved.x = e.clientX;
        moved.y = e.clientY;
      } else if (Math.abs(e.clientX - moved.x) + Math.abs(e.clientY - moved.y) > 30) setActive(false);
    };
    window.addEventListener('keydown', stop, true);
    window.addEventListener('pointerdown', stop, true);
    window.addEventListener('pointermove', onMove, true);
    return () => {
      window.removeEventListener('keydown', stop, true);
      window.removeEventListener('pointerdown', stop, true);
      window.removeEventListener('pointermove', onMove, true);
    };
  }, [active]);

  // Titels ophalen zodra hij start: uit je bibliotheek, trending, of allebei door elkaar
  useEffect(() => {
    if (!active) return;
    const src = prefs.ssSource ?? 'mix';
    const libs = libraries.filter((l) => l.type === 'movie' || l.type === 'show');
    const fromLib = src === 'trending' ? Promise.resolve([] as Slide[]) : Promise.all(libs.map((l) => plex.libraryItems(l.key, { sort: 'random', start: 0, size: 20 }).then((r) => r.items).catch(() => [] as PlexItem[])))
      .then((lists) => lists.flat().filter((x) => x.art).map((x): Slide => ({
        key: `p:${x.ratingKey}`, title: x.title, year: x.year ?? null, overview: x.summary ?? null, image: plex.image(x.art, 1920, 1080) ?? null,
        ref: tmdbRef(x), tag: 'Uit je bibliotheek', kind: x.type === 'show' ? 'Serie' : 'Film', score: x.audienceRating ?? null, genres: (x.Genre ?? []).slice(0, 2).map((g) => g.tag),
      })));
    const fromTrending = src === 'library' ? Promise.resolve([] as Slide[]) : layoutApi.items({ kind: 'tmdb', list: 'trending_week', media: 'all' }, 20, 'all')
      .then((r) => r.items.filter((x) => x.backdrop).map((x): Slide => ({
        key: `t:${x.type}:${x.tmdbId}`, title: x.title, year: x.year, overview: x.overview, image: (x.backdrop ?? '').replace('/w780/', '/w1280/'),
        ref: { type: x.type, tmdbId: x.tmdbId }, tag: x.type === 'tv' ? 'Trending series deze week' : 'Trending films deze week', kind: x.type === 'tv' ? 'Serie' : 'Film', score: x.score ?? null, genres: [],
      })))
      .catch(() => [] as Slide[]);
    Promise.all([fromLib, fromTrending]).then(([a, b]) => {
      const mixed = [...a, ...b].sort(() => Math.random() - 0.5).slice(0, 40);
      setSlides(mixed);
      setI(0);
    }).catch(() => setSlides([]));
    // Wat er deze week uitkomt (kalender)
    if (prefs.ssParts?.upcoming !== false || prefs.ssParts?.countdown !== false) {
      calendarApi.get(7, 0).then((d) => setUpcoming(d.episodes)).catch(() => setUpcoming([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, plex, libraries]);

  useEffect(() => {
    if (!active) return;
    const a = window.setInterval(() => setI((x) => x + 1), 15_000);
    const c = window.setInterval(() => setNow(new Date()), 15_000);
    setNow(new Date());
    return () => {
      window.clearInterval(a);
      window.clearInterval(c);
    };
  }, [active]);

  const cur = slides.length ? slides[i % slides.length] : null;
  const meta = useMeta(active && cur?.ref && prefs.ssParts?.info !== false ? cur.ref : null);

  if (!active) return null;
  const parts = prefs.ssParts ?? { info: true, clock: true, upcoming: true, countdown: true };
  const img = cur?.image ?? undefined;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const week = Array.from({ length: 7 }, (_, d) => new Date(today.getTime() + d * 86_400_000));
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const eps = (upcoming ?? []).filter((e) => e.date >= ymd(today)).sort((x, y) => (x.airTime ?? Date.parse(x.date)) - (y.airTime ?? Date.parse(y.date)));
  const nextEp = eps.find((e) => (e.airTime ?? Date.parse(`${e.date}T23:59:00`)) > now.getTime()) ?? eps[0];
  const when = (e: CalEpisode) => (e.date === ymd(today) ? 'Vandaag' : e.date === ymd(week[1]) ? 'Morgen' : new Date(`${e.date}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'long' }));
  const left = nextEp?.airTime ? nextEp.airTime - now.getTime() : null;
  const genres = cur?.genres.length ? cur.genres : (meta?.genres ?? []).slice(0, 2);

  return (
    <div className="screensaver ss2" role="dialog" aria-label="Screensaver. Druk op een toets om verder te gaan.">
      {img && <div key={`${cur!.key}:${i}`} className="ss-slide" style={{ backgroundImage: `url("${img}")` }} />}
      <div className="ss-shade" />
      <div className="ss2-brand">tu<span>media</span><em>Even pauze</em></div>

      {parts.info && cur && (
        <div className="ss2-info" key={`i${cur.key}:${i}`}>
          <span className="ss2-tag">● {cur.tag}</span>
          {meta?.logo ? <img className="ss2-logo" src={meta.logo} alt={cur.title} /> : <strong className="ss2-name">{cur.title}</strong>}
          {meta?.logo && <span className="ss2-sub">{cur.title}</span>}
          <span className="ss2-meta">{[...genres, cur.kind, cur.year, cur.score ? `★ ${cur.score.toFixed(1)}` : null].filter(Boolean).join('  ·  ')}</span>
          {(meta?.overview ?? cur.overview) && <p className="ss2-plot">{meta?.overview ?? cur.overview}</p>}
        </div>
      )}

      {(parts.clock || parts.upcoming || parts.countdown) && (
        <aside className="ss2-side">
          {parts.clock && (
            <div className="ss2-clock">
              <strong>{now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</strong>
              <span><b>{now.toLocaleDateString('nl-NL', { weekday: 'short' })}</b>{now.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          )}
          {parts.upcoming && eps.length > 0 && (
            <div className="ss2-week">
              <div className="ss2-week-head"><span>{now.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}</span><strong>Binnenkort</strong></div>
              <div className="ss2-days">
                {week.map((d) => (
                  <span key={ymd(d)} className={ymd(d) === ymd(today) ? 'is-today' : ''}>
                    <small>{d.toLocaleDateString('nl-NL', { weekday: 'short' })}</small>
                    <b>{d.getDate()}</b>
                    <i className={eps.some((e) => e.date === ymd(d)) ? 'has' : ''} />
                  </span>
                ))}
              </div>
              <ul className="ss2-eps">
                {eps.slice(0, 3).map((e) => (
                  <li key={e.id}>
                    <span className="ss2-ep-text">
                      <strong>{e.show.title}</strong>
                      <span>S{String(e.season).padStart(2, '0')}E{String(e.episode).padStart(2, '0')}</span>
                      <em>{when(e)}{e.airTime ? ` · ${new Date(e.airTime).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}` : ''}</em>
                    </span>
                    {e.show.poster && <img src={e.show.poster} alt="" />}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {parts.countdown && nextEp && (
            <div className="ss2-count">
              <strong>{left !== null && left > 0 ? countdown(left) : when(nextEp)}</strong>
              <span>{left !== null && left <= 0 ? 'Nu uit' : 'Tot de volgende aflevering'}<b>{nextEp.show.title}</b></span>
            </div>
          )}
        </aside>
      )}
    </div>
  );
}

interface Slide {
  key: string;
  title: string;
  year: number | null;
  overview: string | null;
  image: string | null;
  ref: { type: 'movie' | 'tv'; tmdbId: number } | null;
  tag: string;
  kind: string;
  score: number | null;
  genres: string[];
}

function countdown(ms: number): string {
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} u ${m % 60} m`;
  return `${Math.floor(h / 24)} d ${h % 24} u`;
}

/** Kleine klok voor in het menu (aan te zetten in de skin-studio). */
export function NavClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(t);
  }, []);
  return <span className="nav-clock" aria-label={`Het is ${now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`}>{now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}</span>;
}
